import React, { useState } from 'react';
import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Button, Input, Select, Textarea, Badge } from '../../components/ui';

/**
 * One repeatable section of the shift report.
 *
 * Each section used to be tinted a different pastel (blue / green / yellow /
 * orange / red / purple) to tell them apart. Structure does that job now — a
 * titled, collapsible card with a row count — which frees those colours to mean
 * something and keeps a 538-line form navigable.
 */
function ShiftReportSection({
  title,
  rows,
  fields,
  sectionKey,
  onRowChange,
  onAddRow,
  onRemoveRow,
  collapsible = true,
}) {
  const [open, setOpen] = useState(true);

  const sectionRowClass = {
    scr: 'border-indigo-200 bg-indigo-50/70',
    microphoneStationGroup: 'border-sky-200 bg-sky-50/70',
    correctiveMaintenance: 'border-emerald-200 bg-emerald-50/70',
    systemFaultLog: 'border-amber-200 bg-amber-50/70',
    ppm: 'border-violet-200 bg-violet-50/70',
    others: 'border-rose-200 bg-rose-50/70',
  }[sectionKey] || 'border-border bg-muted/40';

  const renderField = (field, row, index) => {
    // SCR numbers are derived from the selected tag, never typed.
    const isScrNumberField = sectionKey === 'scr' && field.name === 'number';
    const readOnly = isScrNumberField || field.readOnly;

    const common = {
      name: field.name,
      label: field.label,
      value: row[field.name] ?? '',
      onChange: (event) => onRowChange(event, index, sectionKey),
      disabled: field.disabled || readOnly,
    };

    if (field.type === 'select') {
      return (
        <Select key={field.name} {...common}>
          {(field.options || []).map((option) => {
            const value = typeof option === 'string' ? option : option.value;
            const label = typeof option === 'string' ? option : option.label;
            const disabled = typeof option === 'object' ? Boolean(option.disabled) : false;
            return (
              <option key={value} value={value} disabled={disabled}>
                {label}
              </option>
            );
          })}
          {(field.groupedOptions || []).map((group) => (
            <optgroup key={group.label} label={group.label}>
              {(group.options || []).map((option) => {
                const value = typeof option === 'string' ? option : option.value;
                const label = typeof option === 'string' ? option : option.label;
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </optgroup>
          ))}
        </Select>
      );
    }

    if (field.type === 'textarea') {
      return <Textarea key={field.name} {...common} rows={2} className="sm:col-span-2 lg:col-span-4" />;
    }

    return <Input key={field.name} {...common} type={field.type || 'text'} readOnly={readOnly} />;
  };

  const body = (
    <>
      <div className="space-y-3">
        {rows.map((row, index) => (
          <div key={index} className={`rounded-lg border p-3 sm:p-4 ${sectionRowClass}`}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-fg">
                Row {index + 1}
              </span>
              {index > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={DeleteOutline}
                  onClick={() => onRemoveRow(sectionKey, index)}
                >
                  Remove
                </Button>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {fields.map((field) => renderField(field, row, index))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex justify-end">
        <Button type="button" variant="secondary" icon={AddOutlined} onClick={() => onAddRow(sectionKey)}>
          Add row
        </Button>
      </div>
    </>
  );

  if (!title) {
    return <section className="mb-6">{body}</section>;
  }

  return (
    <section className="mb-4 overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
      <button
        type="button"
        onClick={() => collapsible && setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-11 w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors duration-fast hover:bg-muted"
      >
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <Badge variant="neutral" size="sm">
          {rows.length}
        </Badge>
        {collapsible && (
          <ExpandMore
            style={{ fontSize: 20 }}
            aria-hidden="true"
            className={`ml-auto shrink-0 text-muted-fg transition-transform duration-base ${
              open ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {open && <div className="border-t border-border p-4">{body}</div>}
    </section>
  );
}

export default ShiftReportSection;
